import complaintService from "@/views/complaint/complaintService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";

import type { Complaint, ComplaintNew, ComplaintQuery, CanDeleteComplaint } from "./complaint";
import type { AxiosResponse } from "axios";

const URL = "complaints";
const ENTITY_NAME = "Complaint";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<Complaint>(apiHosts.AQMS, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)

 */
const getComplaints = (params: ComplaintQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {Int} id
 */
const getComplaintById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const getNewComplaint = async (ids: ComplaintIds) => {
  const defaultApp = {
    data: {
      ...complaintService.getInitialData(),
      ...ids,
    },
  } as AxiosResponse<ComplaintNew, any>;

  return defaultApp;
};

/**
 *
 * @param {Object} ids Object having either an `id` for an Application or a `facilitySiteId`, `permitId`, and type
 * @param {ApiOptions} config
 * @returns {api.get}
 */

type ComplaintIds = {
  id?: string | number;
};
const getComplaintByIdOrNew = (ids: ComplaintIds, config = {}) => {
  if (!ids.id) {
    return getNewComplaint(ids);
  } else {
    return getComplaintById(ids.id, config) as Promise<AxiosResponse<ComplaintNew, any>>;
  }
};

const createComplaint = (complaint: Complaint, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, complaint, {
    successToastCb,
    errorToastCb,
  });
};

const updateComplaint = (complaint: Complaint, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + complaint.id, complaint, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {Complaint} complaint This cannot be a 'ref' to a complaint
 */
const saveComplaint = (complaint: Complaint, config = {}) => {
  if (complaint.id) {
    return updateComplaint(complaint, config);
  } else {
    return createComplaint(complaint, config);
  }
};

/**
 * @param {Int} id
 */
const deleteComplaintById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const canDeleteApi = apiService<CanDeleteComplaint, Paging<CanDeleteComplaint>>(apiHosts.AQMS, axiosParams);
const canDeleteComplaint = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return canDeleteApi.get(URL + "/can-delete/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export type ComplaintsSummary = {
  openFail: number;
  openTotal: number;
  closedFail: number;
  closedTotal: number;
  pendingTotal: number;
};
const summaryApi = apiService<ComplaintsSummary>(apiHosts.AQMS, axiosParams);

export type ComplaintSummaryQuery = {
  month?: number;
  responseHours?: number;
  closedHours?: number;
};

/**
 * @param {ComplaintSummaryQuery} params Query parameters, varies (TODO improve this)

 */
const getComplaintsSummary = (params: ComplaintSummaryQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return summaryApi.get(url + "/summary/results", {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

export {
  deleteComplaintById,
  getComplaints,
  getComplaintById,
  getNewComplaint,
  getComplaintByIdOrNew,
  createComplaint,
  updateComplaint,
  saveComplaint,
  canDeleteComplaint,
  getComplaintsSummary,
};
